import type { DashboardMode, DrilldownFilterPresetDto } from '@nexus/contracts';
import { DrilldownFilterPresetPayloadSchema } from '@nexus/contracts';
import {
  appliedStateToExploreSearch,
  exploreSearchEquals,
} from '@/features/dashboard/lib/explore-search';
import { useDashboardFiltersStore } from '@/features/dashboard/stores/dashboardFilters.store';
import { withSnapshotDates } from '@/features/dashboard/lib/explore-date-presets';
import type { CompetitorFilters, MyBrandFilters } from '@/features/dashboard/types/dashboard.types';
import {
  DEFAULT_COMPETITOR_FILTERS,
  DEFAULT_MY_BRAND_FILTERS,
} from '@/features/dashboard/types/dashboard.types';
import {
  type DrilldownFiltersState,
  useDrilldownFiltersStore,
} from '@/features/drilldown/stores/drilldownFilters.store';

/** Include custom dates so a leftover 90d token cannot match a saved custom window. */
const PRESET_MATCH_SEARCH = { persistCustomWindow: true } as const;

function competitorsPageDraftKey(state: DrilldownFiltersState): string {
  return JSON.stringify({
    objectType: state.objectType,
    effectiveStatus: state.effectiveStatus,
    segment1: state.segment1,
    segment2: state.segment2,
    segment3: state.segment3,
    advancedFilters: state.advancedFilters,
    competitorSortMode: state.competitorSortMode,
    competitorSortDirection: state.competitorSortDirection,
    viewMode: state.viewMode,
  });
}

function myBrandPageDraftKey(state: DrilldownFiltersState): string {
  return JSON.stringify({
    myBrandObjectType: state.myBrandObjectType,
    myBrandEffectiveStatus: state.myBrandEffectiveStatus,
    audienceSegment: state.audienceSegment,
    myBrandSegment1: state.myBrandSegment1,
    myBrandSegment2: state.myBrandSegment2,
    myBrandSegment3: state.myBrandSegment3,
    myBrandAdvancedFilters: state.myBrandAdvancedFilters,
    myBrandKpis: state.myBrandKpis,
    myBrandSortMode: state.myBrandSortMode,
    myBrandSortDirection: state.myBrandSortDirection,
    viewMode: state.viewMode,
  });
}

export function selectDrilldownDraftKey(
  state: DrilldownFiltersState,
  mode: DashboardMode,
): string {
  return mode === 'my-brand' ? myBrandPageDraftKey(state) : competitorsPageDraftKey(state);
}

export function drilldownPresetPageKey(
  preset: DrilldownFilterPresetDto,
): string {
  const { filters } = preset;
  if (filters.mode === 'my-brand') {
    return JSON.stringify(filters.page);
  }
  return JSON.stringify(filters.page);
}

export function buildCurrentDrilldownPresetPayload(mode: DashboardMode) {
  const drilldown = useDrilldownFiltersStore.getState();
  const dashboard = useDashboardFiltersStore.getState();

  if (mode === 'my-brand') {
    return DrilldownFilterPresetPayloadSchema.parse({
      mode: 'my-brand',
      page: {
        myBrandObjectType: drilldown.appliedMyBrandObjectType,
        myBrandEffectiveStatus: drilldown.appliedMyBrandEffectiveStatus,
        audienceSegment: drilldown.appliedAudienceSegment,
        myBrandSegment1: drilldown.appliedMyBrandSegment1,
        myBrandSegment2: drilldown.appliedMyBrandSegment2,
        myBrandSegment3: drilldown.appliedMyBrandSegment3,
        myBrandAdvancedFilters: drilldown.appliedMyBrandAdvancedFilters,
        myBrandKpis: drilldown.myBrandKpis,
        myBrandSortMode: drilldown.myBrandSortMode,
        myBrandSortDirection: drilldown.myBrandSortDirection,
        viewMode: drilldown.viewMode,
      },
      global: dashboard.appliedMyBrandFilters,
    });
  }

  return DrilldownFilterPresetPayloadSchema.parse({
    mode: 'competitors',
    page: {
      objectType: drilldown.appliedObjectType,
      effectiveStatus: drilldown.appliedEffectiveStatus,
      segment1: drilldown.appliedSegment1,
      segment2: drilldown.appliedSegment2,
      segment3: drilldown.appliedSegment3,
      advancedFilters: drilldown.appliedAdvancedFilters,
      competitorSortMode: drilldown.competitorSortMode,
      competitorSortDirection: drilldown.competitorSortDirection,
      viewMode: drilldown.viewMode,
    },
    global: dashboard.appliedCompetitorFilters,
  });
}

export function applyDrilldownFilterPreset(preset: DrilldownFilterPresetDto): void {
  const { filters } = preset;
  const dashboard = useDashboardFiltersStore.getState();

  useDrilldownFiltersStore.getState().loadPresetPageState(filters.mode, filters.page);

  if (filters.mode === 'my-brand') {
    useDashboardFiltersStore.getState().hydrateFromApplied({
      mode: 'my-brand',
      competitorFilters: dashboard.appliedCompetitorFilters,
      myBrandFilters: withSnapshotDates(filters.global as MyBrandFilters),
    });
  } else {
    useDashboardFiltersStore.getState().hydrateFromApplied({
      mode: 'competitors',
      competitorFilters: withSnapshotDates(filters.global as CompetitorFilters),
      myBrandFilters: dashboard.appliedMyBrandFilters,
    });
  }
  useDashboardFiltersStore.getState().applyFilters();
  useDashboardFiltersStore.getState().setMode(filters.mode);
}

export function drilldownPresetMatchesCurrentFilters(preset: DrilldownFilterPresetDto): boolean {
  const drilldown = useDrilldownFiltersStore.getState();
  const dashboard = useDashboardFiltersStore.getState();
  const presetMode = preset.filters.mode;

  if (selectDrilldownDraftKey(drilldown, presetMode) !== drilldownPresetPageKey(preset)) {
    return false;
  }

  const live =
    presetMode === 'my-brand'
      ? appliedStateToExploreSearch(
          'my-brand',
          dashboard.competitorFilters,
          dashboard.myBrandFilters,
          PRESET_MATCH_SEARCH,
        )
      : appliedStateToExploreSearch(
          'competitors',
          dashboard.competitorFilters,
          dashboard.myBrandFilters,
          PRESET_MATCH_SEARCH,
        );
  const wanted =
    presetMode === 'my-brand'
      ? appliedStateToExploreSearch(
          'my-brand',
          DEFAULT_COMPETITOR_FILTERS,
          withSnapshotDates(preset.filters.global as MyBrandFilters),
          PRESET_MATCH_SEARCH,
        )
      : appliedStateToExploreSearch(
          'competitors',
          withSnapshotDates(preset.filters.global as CompetitorFilters),
          DEFAULT_MY_BRAND_FILTERS,
          PRESET_MATCH_SEARCH,
        );

  return exploreSearchEquals(live, wanted);
}
